import {
  showToast,
  updateToastProgress,
  updateToastMessage,
  hideToast,
  getCurrentToastToken,
} from "../components/Toasts";
import EtsReadbackResultDialog from "../modelDialogs/EtsReadbackResultDialog";
import axios, { LocalEtsAppUrl, etsFetch, hideLoader, showLoader } from "../utils/axios";

import JSZip from "jszip";
import Room from "../models/Room";
import Logger from "../Logger";

import type BaseModel from "../models/BaseModel";
import Device from "../models/Device";

import { encodeToCP437 } from "../utils/UmlautEncodeDecode";
import BaseDialog from "../modelDialogs/BaseDialog";
import { itx } from "../translationItx/itx";
import { cleanEncode } from "../utils/CommonUtils";
import { generateGroupAddresses } from "../addressGeneration/addressGenerator";
import {
  etsEventEmitter,
  etsWebSocket,
  getActiveEtsSocket,
  setEtsConnectionMode,
  etsConnectionMode,
  openLocalEtsWebSocket,
  closeLocalEtsWebSocket,
  localEtsWebSocket,
} from "./etsWebSocket";

import EtsDialog from "../components/EtsDialog";
import ConfirmDialog from "../modelDialogs/ConfirmDialog";
import { createKnxData, createKnxMetaData, downloadData } from "../export";
import { EtsSyncConfig } from "../models/EtsSyncConfig";
import { isUiModeDeveloper } from "../uiMode";
// import { applyEtsReadbackData } from "./etsReadback";

const logger = new Logger();

const yieldToUI = () => new Promise<void>((r) => setTimeout(r, 0));
const SHOW_ETS_PUSH_WARNINGS_AFTER_PUSH = false;

function safeReadbackDownloadName(readbackData: any): string {
  const name = String(readbackData?.projectName || readbackData?.etsProjectName || readbackData?.name || "ets-readback")
    .replace(/[<>:"/\\|?*]+/g, "-")
    .trim();
  return name || "ets-readback";
}

async function showNoReadbackDifferencesDialog() {
  await state.dialogManager.openDialog(
    <ConfirmDialog
      title={itx("etsReadback.noDifferencesTitle", "ETS Readback")}
      text={itx("etsReadback.noDifferencesFound", "No differences found. The Estada project is already in sync with ETS.")}
      okCaption={itx("ok", "OK")}
      noAbortButton
      hideXButton
    />,
    true,
    false,
  );
}

var etsOnline = false;
type EtsRelayStatus = {
  status: boolean;
  pin: string;
  version: string;
  buildDate: string;
  projectName?: string;
  projectId?: string;
  projectGuid?: string;
};
export type EtsLocalProject = { projectId: string; projectGuid: string; projectName: string; selected?: boolean };
type EtsLocalStatus = {
  status: boolean;
  version: string;
  buildDate: string;
  projectName?: string;
  projectId?: string;
  projectGuid?: string;
  selectedProjectId?: string;
  projects?: EtsLocalProject[];
};
type EtsUploadWaitResult = { responseData?: any; localCompletionFeedbackMissing?: boolean };

const ETS_RELAY_OFFLINE_STATUS: EtsRelayStatus = { status: false, pin: "", version: "", buildDate: "" };
const ETS_LOCAL_OFFLINE_STATUS: EtsLocalStatus = { status: false, version: "", buildDate: "" };
const ETS_UPLOAD_COMPLETION_TIMEOUT_MS = 15 * 60 * 1000;
const ETS_LOCAL_UPLOAD_FEEDBACK_GRACE_MS = 20 * 1000;
const ETS_LOCAL_UPLOAD_FEEDBACK_POLL_MS = 500;
const ETS_LOCAL_UPLOAD_RESPONSIVE_CONFIRMATIONS = 2;
const ETS_UPLOAD_TOAST_MAX_VISIBLE_MS = 90 * 1000;

let lastSuccessfulRelayUrl = "";
let lastSuccessfulRelayRoom = "";
let lastSuccessfulRelayStatus: EtsRelayStatus | undefined;
let relayPingMissCount = 0;
const RELAY_PING_MISSES_BEFORE_FALLBACK = 1;
let activeUploadAbortController: AbortController | undefined;
let activeUploadCleanup: (() => Promise<void>) | undefined;
let syncResetRequested = false;

export type EtsReadbackSource = "ets" | "json";
export type EtsReadbackMode = "full" | "parameters";

// Prevents concurrent readback operations
let _readbackActive = false;
let _readbackSource: EtsReadbackSource | undefined;

// Prevents concurrent push-to-ETS operations
let _syncActive = false;
let _lastEtsPushWasLocal = false;

// Stores the ImportSummary of the last completed readback so EtsDialog can show it
let _lastReadbackSummary: any | null = null;
let _lastEtsPushDeveloperTiming: any | null = null;

export function getLastReadbackSummary() {
  return _lastReadbackSummary;
}

export function getLastEtsPushDeveloperTiming() {
  return _lastEtsPushDeveloperTiming;
}

function setLastEtsPushDeveloperTiming(timing: any) {
  _lastEtsPushDeveloperTiming = timing || null;
  if (isUiModeDeveloper() && timing) {
    console.info("[ETS push timing]", timing); // no itx
  }
  etsEventEmitter.emit("pushDeveloperTimingChange", _lastEtsPushDeveloperTiming); // no itx
}

function isCompletedUploadStatus(status: any): boolean {
  return status === "OK" || status === "PARTIAL"; // no itx
}

export function isReadbackActive() {
  return _readbackActive;
}

export function getReadbackSource() {
  return _readbackSource;
}

export function isSyncActive() {
  return _syncActive;
}

export function wasLastEtsPushLocal() {
  return _lastEtsPushWasLocal;
}

export async function resetEtsOperationState() {
  syncResetRequested = true;
  activeUploadAbortController?.abort();
  activeUploadAbortController = undefined;
  if (activeUploadCleanup) {
    await activeUploadCleanup();
    activeUploadCleanup = undefined;
  }
  await hideToast();
  _syncActive = false;
  _readbackActive = false;
  _readbackSource = undefined;
  etsEventEmitter.emit("syncProgressChange", false); // no itx
  etsEventEmitter.emit("readbackProgressChange", false, undefined); // no itx
}

var addressOverflow = false;

function addressOverflowTextMsgT() {
  return state.translate(
    "export.addressOverflowText",
    "Address Level #lvl contains too many elements. Please reduce number of #items.",
  );
}

function delay(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

async function waitForLocalEtsResponsiveAfterUploadGrace(startedAt: number): Promise<void> {
  const graceRemaining = Math.max(0, ETS_LOCAL_UPLOAD_FEEDBACK_GRACE_MS - (Date.now() - startedAt));
  await delay(graceRemaining);

  let confirmations = 0;
  while (confirmations < ETS_LOCAL_UPLOAD_RESPONSIVE_CONFIRMATIONS) {
    const localStatus = await checkIfLocalEtsOnline();
    confirmations = localStatus.status ? confirmations + 1 : 0;
    if (confirmations >= ETS_LOCAL_UPLOAD_RESPONSIVE_CONFIRMATIONS) return;
    await delay(ETS_LOCAL_UPLOAD_FEEDBACK_POLL_MS);
  }
}

async function waitForUploadResult(knxData: any): Promise<EtsUploadWaitResult> {
  const socket = getActiveEtsSocket();
  if (!socket) return {};

  const startedAt = Date.now();
  const uploadAbortController = new AbortController();
  activeUploadAbortController = uploadAbortController;
  const uploadResultPromise: Promise<EtsUploadWaitResult> = socket
    .sendCommandAsync(
      "upload", // no itx
      knxData,
      ETS_UPLOAD_COMPLETION_TIMEOUT_MS,
      uploadAbortController.signal,
    )
    .then((responseData) => ({ responseData }));

  if (etsConnectionMode !== "local") {
    return uploadResultPromise;
  }

  const localResponsivePromise: Promise<EtsUploadWaitResult> = waitForLocalEtsResponsiveAfterUploadGrace(
    startedAt,
  ).then(() => ({
    localCompletionFeedbackMissing: true,
  }));
  const result = await Promise.race([uploadResultPromise, localResponsivePromise]);
  if (result.localCompletionFeedbackMissing) {
    uploadAbortController.abort();
  }
  if (activeUploadAbortController === uploadAbortController) {
    activeUploadAbortController = undefined;
  }
  return result;
}

export async function syncWithETS(config?: EtsSyncConfig, pinValue?: string) {
  await syncOperation(config, false);
}

// export async function syncWithETS_out(config?: EtsSyncConfig, pinValue?: string) {
//   try {
//     await syncOperation(config, false);

//     const knxMetadata = createKnxMetaData(config, undefined, pinValue);

//     const resData = await etsWebSocket?.sendCommandAsync("checkOpenedProject", knxMetadata); // no itx

//     if (resData?.status == "OK") {
//       const online = resData?.online == "true";

//       // Abdul todo:
//       // MWE 12.12.2025 was ist onlineExternal
//       //
//       // comment all resullt fields
//       //

//       const clean = resData?.clean == "true";
//       const etsId = resData?.etsId;
//       const etsPin = resData?.projectPin;

//       const title = itx("export.etsSyncConfirmation", "ETS Sync Confirmation");

//       if (online) {
//         // showDialogMsg(title, itx("export.projOnline","A past synced project is already open in ETS. Proceed with Synchronization?"))
//         await syncOperation(config, false, etsPin);
//         // proceed
//       } else {
//         if (clean) {
//           // no devices and no group addresses in ets project so
//           showDialogMsg(
//             title,
//             itx(
//               "export.projNotOnline",
//               "No existing synced project opened in ETS. Continue syncing on currently opened ETS project?",
//             ),
//             true,
//             etsId,
//             knxMetadata,
//             true,
//             config,
//             false,
//             etsPin,
//           );
//           // No existing synced project opened in ETS. Continue syncing on opened ETS project?
//         } else {
//           showDialogMsg(
//             title,
//             itx(
//               "export.anotherWorkingProjectOpenedInEts",
//               "Warning: A different project is active in ETS. To avoid conflicts, it is strongly recommended to sync within a new or previously synced project. Do you still want to proceed?",
//             ),
//             true,
//             etsId,
//             knxMetadata,
//             true,
//             config,
//             false,
//             etsPin,
//           );
//           showToast({
//             text: state.translate("export.ETSOperationClosed", "Operation closed."),
//           });
//         }
//       }
//       return;
//     }
//   } catch (ex) {
//     logger.error("Error fetching data:", ex);
//   }

//   showToast({
//     isError: true,
//     text: state.translate(
//       "export.errorSyncFailed",
//       "Sync failed. Please verify that ETS and Estada Plugin are active.",
//     ),
//   });
// }

//
// Abdul todo:
//  create a readable, multiline message
//

function verifyKnxDataAndCreateToastMessage(knxData: any): string {
  let ToastMsg = "";
  if (!knxData.topology) {
    ToastMsg += state.translate("export.deviceTopology", "Device Topology, ");
  }
  if (!knxData.groupAddresses) {
    ToastMsg += state.translate("export.groupAdresses", " Group Addresses, ");
  }
  if (!knxData.projectStructure) {
    ToastMsg += state.translate("export.builfingStructure", " Building Structure, ");
  }
  if (ToastMsg != "") {
    ToastMsg += state.translate("export.noSetSync", "not set. Proceeding to Sync with ETS App.");
  } else {
    ToastMsg = state.translate("export.syncWithEtsApp", "Syncing with ETS App.");
  }
  return ToastMsg;
}

// no itx
export async function readbackFromETS(
  config?: EtsSyncConfig,
  updateGroupAdresses?: boolean,
  etsPin?: string,
  mode: EtsReadbackMode = "full",
) {
  if (_readbackActive) {
    showToast({ text: itx("etsReadback.alreadyInProgress", "Readback is already in progress..."), keepVisible: false });
    return;
  }
  _readbackActive = true;
  _readbackSource = "ets"; // no itx
  etsEventEmitter.emit("readbackProgressChange", true, _readbackSource); // no itx
  try {
    const parametersOnly = mode === "parameters";
    const knxData = createKnxMetaData(config, undefined, etsPin);
    if (!knxData) {
      return;
    }
    (knxData as any).readbackMode = mode;

    await hideToast();

    // Show modal waiting toast while ETS plugin processes the readback (can take many seconds)
    const readingTitle = parametersOnly
      ? itx("etsReadback.readingParametersTitle", "ETS Parameter Readback")
      : itx("etsReadback.readingTitle", "ETS Readback");
    const readingText = parametersOnly
      ? itx("etsReadback.readingParametersFromETS", "Reading device parameters from ETS...")
      : itx("etsReadback.readingFromETS", "Reading project from ETS...");
    await showToast({
      title: readingTitle,
      text: readingText,
      keepVisible: true,
      modal: true,
    });
    const waitingToastToken = getCurrentToastToken();
    await yieldToUI();

    // Append elapsed seconds to the waiting message so the user sees progress
    let elapsedSeconds = 0;
    const baseWaitText = readingText;
    const waitMessageInterval = setInterval(() => {
      elapsedSeconds += 1;
      updateToastMessage(`${baseWaitText} (${elapsedSeconds}s)`, waitingToastToken);
    }, 1000);

    const waitingToastStartTime = Date.now();
    let responseData: any;
    try {
      // Use a generous timeout (5 min) — large projects can take 30+ seconds on the C# side
      responseData = await getActiveEtsSocket()?.sendCommandAsync("readback", knxData, 5 * 60 * 1000);
    } finally {
      clearInterval(waitMessageInterval);
    }

    // Ensure the waiting toast was visible for at least 1s even on an instant (cached) response
    const elapsed = Date.now() - waitingToastStartTime;
    if (elapsed < 1000) {
      await new Promise<void>((r) => setTimeout(r, 1000 - elapsed));
    }

    if (!responseData) {
      // WebSocket timed out or plugin didn't respond
      updateToastMessage(
        itx("etsReadback.timeout", "No response from ETS. Make sure ETS and the Estada plugin are running."),
        waitingToastToken,
      );
      await new Promise<void>((r) => setTimeout(r, 3000));
      await hideToast();
      return;
    }

    // Log for diagnostics: shows the top-level keys without printing potentially large exportData
    console.debug("[readbackFromETS] response keys:", Object.keys(responseData));

    if (!responseData.exportData || Object.keys(responseData.exportData).length === 0) {
      // C# returned a response but without usable export data — likely an exception on the plugin side
      const errMsg =
        Array.isArray(responseData.errors) && responseData.errors.length > 0
          ? responseData.errors.map((e: any) => e?.message ?? String(e)).join("; ")
          : itx("etsReadback.noExportData", "ETS returned no project data. Check the Estada plugin logs.");
      updateToastMessage(errMsg, waitingToastToken);
      await new Promise<void>((r) => setTimeout(r, 4000));
      await hideToast();
      await showToast({ isError: true, text: errMsg, keepVisible: true });
      return;
    }

    updateToastMessage(itx("etsReadback.responseReceived", "Response received, ready to apply..."), waitingToastToken);
    await yieldToUI();
    await hideToast();
    await yieldToUI();

    if (responseData.exportData) {
      if (isUiModeDeveloper()) {
        downloadData(JSON.stringify(responseData.exportData), ".ets-readback.json", safeReadbackDownloadName(responseData.exportData)); // no itx
      }

      await showToast({
        title: itx("etsReadback.previewTitle", "ETS Readback"),
        text: itx("etsReadback.preparingPreview", "Preparing readback preview..."),
        keepVisible: true,
        modal: true,
      });
      await yieldToUI();
      const { buildEtsReadbackPreviewSummary, hasImportSummaryChanges } = await import("../etsFunctions/etsReadback");
      let previewSummary: any;
      try {
        previewSummary = buildEtsReadbackPreviewSummary(responseData.exportData, { mode });
      } finally {
        await hideToast();
      }
      if (!hasImportSummaryChanges(previewSummary)) {
        await showNoReadbackDifferencesDialog();
        return;
      }
      const shouldApply = await state.dialogManager.openDialog(
        <EtsReadbackResultDialog summary={previewSummary} preview readbackData={responseData.exportData} />,
        true,
        false,
      );
      if (shouldApply) {
        // Show toast immediately so user sees feedback before the heavy import starts
        await showToast({
          title: itx("etsReadback.importingTitle", "ETS Import"),
          text: itx("etsReadback.startingImport", "Starting import..."),
          keepVisible: true,
          progress: true,
        });
        await yieldToUI();

        const { applyEtsReadbackData } = await import("../etsFunctions/etsReadback");
        let summary: any;
        try {
          summary = await applyEtsReadbackData(responseData.exportData, { mode });
        } finally {
          // Always hide the progress toast when import is done (success or error)
          await hideToast();
        }

        if (summary) _lastReadbackSummary = summary;
        if (hasImportSummaryChanges(summary)) {
          await state.dialogManager.openDialog(
            <EtsReadbackResultDialog summary={summary} readbackData={responseData.exportData} />,
            true,
          );
        } else {
          await showNoReadbackDifferencesDialog();
        }
      } else {
        showToast({
          text: itx("export.etsReadbackSkipped", "ETS readback data skipped."),
        });
      }
    }
  } finally {
    _readbackActive = false;
    _readbackSource = undefined;
    etsEventEmitter.emit("readbackProgressChange", false, undefined); // no itx
    reopenEtsDialog();
  }
}

export async function readbackFromJSON(jsonData: any, mode: EtsReadbackMode = "full") {
  if (_readbackActive) {
    showToast({ text: itx("etsReadback.alreadyInProgress", "Readback is already in progress..."), keepVisible: false });
    return;
  }
  if (jsonData) {
    _readbackActive = true;
    _readbackSource = "json"; // no itx
    etsEventEmitter.emit("readbackProgressChange", true, _readbackSource); // no itx
    try {
      await showToast({
        title: itx("etsReadback.previewTitle", "ETS Readback"),
        text: itx("etsReadback.preparingPreview", "Preparing readback preview..."),
        keepVisible: true,
        modal: true,
      });
      await yieldToUI();
      const { buildEtsReadbackPreviewSummary, hasImportSummaryChanges } = await import("../etsFunctions/etsReadback");
      let previewSummary: any;
      try {
        previewSummary = buildEtsReadbackPreviewSummary(jsonData, { mode });
      } finally {
        await hideToast();
      }
      if (!hasImportSummaryChanges(previewSummary)) {
        await showNoReadbackDifferencesDialog();
        return;
      }
      const shouldApply = await state.dialogManager.openDialog(
        <EtsReadbackResultDialog summary={previewSummary} preview readbackData={jsonData} />,
        true,
        false,
      );

      if (shouldApply) {
        // Show toast immediately so user sees feedback before the heavy import starts
        await showToast({
          title: itx("etsReadback.importingTitle", "ETS Import"),
          text: itx("etsReadback.startingImport", "Starting import..."),
          keepVisible: true,
          progress: true,
        });
        await yieldToUI();

        const { applyEtsReadbackData } = await import("../etsFunctions/etsReadback");
        let summary: any;
        try {
          summary = await applyEtsReadbackData(jsonData, { mode });
        } finally {
          await hideToast();
        }

        if (summary) _lastReadbackSummary = summary;
        if (hasImportSummaryChanges(summary)) {
          await state.dialogManager.openDialog(<EtsReadbackResultDialog summary={summary} readbackData={jsonData} />, true);
        } else {
          await showNoReadbackDifferencesDialog();
        }
      } else {
        showToast({
          text: itx("export.etsReadbackSkipped", "ETS readback data skipped."),
        });
      }
    } finally {
      _readbackActive = false;
      _readbackSource = undefined;
      etsEventEmitter.emit("readbackProgressChange", false, undefined); // no itx
      reopenEtsDialog();
    }
  }
}

export async function syncOperation(config?: EtsSyncConfig, updateGroupAdresses?: boolean, etsPin?: string) {
  if (_syncActive) {
    showToast({
      text: itx("etsSyncAndExport.syncAlreadyInProgress", "Push to ETS already in progress, please wait..."),
    });
    return;
  }
  _syncActive = true;
  _lastEtsPushWasLocal = etsConnectionMode === "local";
  syncResetRequested = false;

  const syncStartTime = Date.now();
  let uploadElapsedTimer: ReturnType<typeof setInterval> | null = null;
  let uploadToastAutoHideTimer: ReturnType<typeof setTimeout> | null = null;
  let uploadToastActive = false;

  const stopUploadToast = async () => {
    if (uploadToastAutoHideTimer) {
      clearTimeout(uploadToastAutoHideTimer);
      uploadToastAutoHideTimer = null;
    }
    if (uploadElapsedTimer) {
      clearInterval(uploadElapsedTimer);
      uploadElapsedTimer = null;
    }
    if (uploadToastActive) {
      uploadToastActive = false;
      await hideToast();
    }
  };
  activeUploadCleanup = stopUploadToast;

  try {
    // state.dialogManager.closeDialog();
    etsEventEmitter.emit(
      "syncProgressChange", // no itx
      true,
    );

    // Group Address regeneration (silently) before syncing
    if (updateGroupAdresses) {
      await generateGroupAddresses(undefined, undefined, true);
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////
    // for statistic
    //////////////////////////////////////////////////////////////////////////////////////////////////

    state.projectMetadata.statistic.numberOfEtsSyncs++;
    /////////////////////////////////////////////////////////////////////////////////////////////////////
    const knxData = await createKnxData(true, config, undefined, etsPin, "Push to ETS"); // no itx
    if (!knxData) {
      return; // finally handles cleanup
    }

    if (addressOverflow) {
      showToast({
        text: addressOverflowTextMsgT(),
      });
      return; // finally handles cleanup
    }

    const ToastMsg = verifyKnxDataAndCreateToastMessage(knxData);
    const getUploadToastText = () => {
      const elapsed = Math.round((Date.now() - syncStartTime) / 1000);
      const extendedText =
        elapsed >= 30 ? ", " + state.translate("export.extendedToastMsg", "This may take a while.") : "";
      return `${ToastMsg}${extendedText} (${elapsed}s)`;
    };

    await showToast({
      text: getUploadToastText(), // no itx
      keepVisible: true,
    });
    uploadToastActive = true;
    const uploadToastToken = getCurrentToastToken();

    // Update the elapsed-seconds counter every second during the upload so the
    // user can see progress even while waiting for the ETS plugin to respond.
    uploadElapsedTimer = setInterval(() => {
      updateToastMessage(getUploadToastText(), uploadToastToken);
    }, 1000);
    uploadToastAutoHideTimer = setTimeout(async () => {
      uploadToastAutoHideTimer = null;
      if (!uploadToastActive || getCurrentToastToken() !== uploadToastToken) return;

      if (uploadElapsedTimer) {
        clearInterval(uploadElapsedTimer);
        uploadElapsedTimer = null;
      }
      uploadToastActive = false;
      await hideToast(uploadToastToken);
      showToast({
        text: itx(
          "etsSyncAndExport.pushStillRunningInBackground",
          "Push is still running in ETS6. The toast was hidden; watch the ETS icon for progress.",
        ),
        time: 6000,
      });
    }, ETS_UPLOAD_TOAST_MAX_VISIBLE_MS);

    const uploadResult = await waitForUploadResult(knxData);
    await stopUploadToast();
    if (activeUploadCleanup === stopUploadToast) {
      activeUploadCleanup = undefined;
    }
    if (syncResetRequested) {
      return;
    }
    const responseData = uploadResult.responseData;

    if (uploadResult.localCompletionFeedbackMissing) {
      showToast({
        text: state.translate(
          "export.etsLocalUploadCompletionFeedbackMissing",
          "Local ETS6 is responsive again, but no completion status was received. Please verify the import result in ETS6.",
        ),
      });
      return;
    }

    if (!responseData) {
      showToast({
        isError: true,
        text: state.translate(
          "export.etsUploadCompletionTimeout",
          "Could not receive completion status from ETS6 in time. Please check ETS6; the import may still have completed.",
        ),
      });
      return;
    }

    if (responseData?.status === "ABORTED") {
      await state.dialogManager.openDialog(
        <ConfirmDialog
          title={itx("etsSyncAndExport.pushCanceledTitle", "Push canceled")}
          text={
            responseData.message ||
            itx(
              "etsSyncAndExport.pushCanceledText",
              "The push to ETS was canceled safely. ETS kept the changes that had already completed, and the remaining import steps were skipped.",
            )
          }
          okCaption={itx("ok", "OK")}
          noAbortButton={true}
        />,
        true,
      );
      return;
    }

    // no itx
    if (!isCompletedUploadStatus(responseData?.status)) {
      console.error("Sync Error");
      showToast({
        isError: true,
        text: state.translate("export.syncFailed", "Sync failed. Please verify that ETS and Estada Plugin are active."),
      });
      return;
    }

    state.projectMetadata.etsId = responseData.projectId;
    state.projectMetadata.etsGuid = responseData.projectGuid;
    state.projectMetadata.etsLog = responseData.logHistory;

    let exportSuccess = true;

    if (responseData?.errors) {
      responseData.errors.map((error: any) => {
        // Capturing specific ETS errors
        if (
          error.message.startsWith("Sync Operation Failed") ||
          error.message.startsWith("Synchronisierungsvorgang fehlgeschlagen")
        ) {
          showToast({
            isError: true,
            text: error.message,
          });
        } else {
          // alert(`${error.message} ${error.stackTrace}`);
          showToast({
            isError: true,
            text: state.translate("export.errorSyncInternal", "Sync failed. An internal error occured in ETS."),
          });
        }
        exportSuccess = false;
      });
    }

    if (exportSuccess) {
      const mappings = Array.isArray(responseData.idMappings) ? responseData.idMappings : [];
      const trueIdMappings = mappings.filter((idMapping: any) => idMapping?.id != null);
      const etsSyncIssueDevices = mappings.filter((item: any) => item?.deviceId != null);
      const importIssues = Array.isArray(responseData.importIssues) ? responseData.importIssues : [];

      trueIdMappings.map((idMappings: any) => {
        const object = state.objectRegistry.getObject(idMappings.id);
        if (object) {
          (object as BaseModel).etsId = idMappings.etsId;
          (object as BaseModel).etsSyncTimestamp = new Date().getDate();
        }
      });

      // if (isBetaVersion() || isUiModeDeveloper()) {
      const sData = JSON.stringify(knxData);

      downloadData(sData, ".estada-push.json"); // no itx

      showToast({
        text:
          responseData.status === "PARTIAL"
            ? itx("export.syncPartial", "Sync with ETS completed with warnings. Please review the details.")
            : state.translate("export.syncSuccessful", "Sync with ETS successful!"),
      });
      setLastEtsPushDeveloperTiming({
        ...(responseData.developerTiming || {}),
        importIssues,
      });

      if (isUiModeDeveloper() && etsSyncIssueDevices.length > 0) await showETSSyncStatsWithCatalogIssues(etsSyncIssueDevices);
      if (SHOW_ETS_PUSH_WARNINGS_AFTER_PUSH && isUiModeDeveloper() && importIssues.length > 0) {
        await showETSPushImportIssues(importIssues);
      }
    }
  } catch (ex) {
    await stopUploadToast();
    logger.error("Error in syncOperation:", ex); // no itx
  } finally {
    await stopUploadToast();
    if (activeUploadCleanup === stopUploadToast) {
      activeUploadCleanup = undefined;
    }
    activeUploadAbortController = undefined;
    syncResetRequested = false;
    _syncActive = false;
    etsEventEmitter.emit("syncProgressChange", false); // no itx
    reopenEtsDialog();
  }
}

function decToHexVersion(version: string): string {
  if (!version) return "0.0";
  return `${Math.floor(parseInt(version) / 16)}.${parseInt(version) % 16}`;
}

export async function showETSSyncStatsWithCatalogIssues(
  etsSyncIssueDevices: any[],
  source: "push" | "export" = "push",
): Promise<void> {
  const title =
    source === "export"
      ? itx("export.ETSExportStats", "ETS export status")
      : itx("export.ETSSyncStats", "ETS push status");
  const mismatchedCatalogDevices = etsSyncIssueDevices.filter((item: any) => item?.issue !== "catalogItemNotFound");
  const catalogItemNotFoundDevices = etsSyncIssueDevices.filter((item: any) => item?.issue === "catalogItemNotFound");
  const missingCatalogHeading = itx(
    "export.catalogItemsNotFoundDevices",
    "The following device(s) were not added in ETS because they were not found in the ETS catalog:",
  );
  const versionMismatchHeading = itx(
    "export.mismatchedCatalogDevices",
    "The following device(s) have a different application version in ETS:",
  );

  await state.dialogManager.openDialog(
    <BaseDialog
      title={title}
      abortCaption={itx("close", "Close")}
      noOkButton={true}
      noMinWidth={true}
      bodyNoMinWidth={true}
      style={{
        width: "min(920px, calc(100vw - 40px))",
        maxWidth: "calc(100vw - 40px)",
      }}
    >
      <div style={{ maxWidth: "100%", overflowWrap: "anywhere", wordBreak: "break-word" }}>
        <div
          style={{
            padding: "20px",
            maxHeight: "400px",
            overflow: "auto",
            maxWidth: "100%",
          }}
        >
          {catalogItemNotFoundDevices.length > 0 && (
            <div style={{ marginBottom: mismatchedCatalogDevices.length > 0 ? "18px" : 0 }}>
              <div style={{ fontWeight: "bold", marginBottom: "8px" }}>{missingCatalogHeading}</div>
              {catalogItemNotFoundDevices.map((item: any, index: number) => {
                const device = state.objectRegistry.getObject(item?.deviceId);
                const label = device
                  ? `${device?.fullTag} ${(device as Device)?.fullname} ${(device as Device)?.fullPhysicalAddress}`
                  : `${item?.deviceName || item?.deviceId || ""} ${item?.physicalAddress || ""}`;
                return (
                  <div key={`catalog-missing-${index}`}>
                    - {label} : {item?.orderNumber || ""} v{decToHexVersion(item?.appVersionEstada)}
                  </div>
                );
              })}
            </div>
          )}
          {mismatchedCatalogDevices.length > 0 && (
            <div>
              <div style={{ fontWeight: "bold", marginBottom: "8px" }}>{versionMismatchHeading}</div>
              {mismatchedCatalogDevices.map((item: any, index: number) => {
                const device = state.objectRegistry.getObject(item?.deviceId);
                const label = device
                  ? `${device?.fullTag} ${(device as Device)?.fullname} ${(device as Device)?.fullPhysicalAddress}`
                  : `${item?.deviceName || item?.deviceId || ""} ${item?.physicalAddress || ""}`;
                return (
                  <div key={`catalog-mismatch-${index}`}>
                    - {label} :{" "}
                    {`v${decToHexVersion(item?.appVersionETS)} (ETS) ; v${decToHexVersion(item?.appVersionEstada)} (Estada)`}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {mismatchedCatalogDevices.length > 0 && (
          <div style={{ padding: "0 20px 8px 20px" }}>
            {itx(
              "export.differentParamConfig",
              "Parameter configuration may be displayed differently in ETS compared to Estada application due to application version mismatch.",
            )}
          </div>
        )}
        <div style={{ padding: "0 20px 20px 20px" }}>
          <span style={{ fontWeight: "bold" }}>{itx("export.recommendedAction", "Recommended action")}</span>:&nbsp;
          <span>
            {catalogItemNotFoundDevices.length > 0
              ? itx(
                  "export.catalogItemsNotFoundRecommendedActionText",
                  "Please download the KNXPROD file from the official manufacturer website for such devices, import it into the ETS catalog, and re-push the project.",
                )
              : itx(
                  "export.recommendedActionText",
                  "For respective devices, use a product with matching application and version as in ETS, if available. If not, please verify the parameter configuration in ETS after synchronization.",
                )}
          </span>
        </div>
      </div>
    </BaseDialog>,
    true,
  );
}

function formatEtsPushIssueDetails(issue: any): string[] {
  const hiddenKeys = new Set([
    "severity",
    "phase",
    "code",
    "message",
    "meaning",
    "device",
    "deviceId",
    "deviceName",
    "physicalAddress",
    "functionName",
    "paramTextImportMode",
    "missingParams",
    "incompleteModuleParams",
    "skippedTextParams",
    "ignoredGroupAddressList",
    "failedLinkList",
    "failedParams",
    "foundParams",
    "requestedParams",
    "skippedTextParamCount",
    "configuredModules",
    "requestedModuleParams",
    "foundModuleParams",
    "moduleParamPasses",
    "modulesProcessed",
    "modulesRetried",
    "orderNumber",
    "ignoredGroupAddressCount",
    "missingGroupAddresses",
    "linkRetryFailures",
    "linked",
    "unlinked",
  ]);
  return Object.entries(issue || {})
    .filter(([key, value]) => !hiddenKeys.has(key) && value !== undefined && value !== null && String(value) !== "")
    .map(([key, value]) => `${key}: ${String(value)}`);
}

function formatEtsPushIssueSummary(issue: any): string[] {
  const lines: string[] = [];
  const add = (label: string, value: any) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      lines.push(`${label}: ${String(value)}`);
    }
  };

  add("Device", issue?.device || [issue?.physicalAddress, issue?.deviceName].filter(Boolean).join(" - "));
  add("Function", issue?.functionName);
  add("Mode", issue?.paramTextImportMode);
  add("Missing", issue?.missingParams || issue?.incompleteModuleParams);
  add("Skipped", issue?.skippedTextParams);
  add("Ignored", issue?.ignoredGroupAddressList);
  add("Failed links", issue?.failedLinkList);
  add("Failed", issue?.failedParams);

  if (issue?.foundParams || issue?.requestedParams || issue?.skippedTextParamCount) {
    const parts = [
      issue?.foundParams ? `set ${issue.foundParams}` : "",
      issue?.skippedTextParamCount ? `skipped ${issue.skippedTextParamCount}` : "",
      issue?.requestedParams ? `requested ${issue.requestedParams}` : "",
    ].filter(Boolean);
    add("Counts", parts.join(", "));
  }

  return lines;
}

function isSecondaryEtsPushIssue(issue: any): boolean {
  return String(issue?.severity || "").toLowerCase() === "secondary";
}

function getEtsPushIssueDisplaySeverity(issue: any): string {
  return isSecondaryEtsPushIssue(issue) ? "WARNING" : "PUSH ERROR";
}

function formatEtsPushImportIssuesText(importIssues: any[]): string {
  if (!Array.isArray(importIssues) || importIssues.length === 0) return "";

  return importIssues
    .map((issue: any, index: number) => {
      const header = `${index + 1}. ${getEtsPushIssueDisplaySeverity(issue)} | ${issue?.phase || "import"} | ${
        issue?.code || "issue"
      }`;
      const details = formatEtsPushIssueDetails(issue);
      return [header, issue?.message || "", ...details.map((detail) => `   ${detail}`)].filter(Boolean).join("\n");
    })
    .join("\n\n");
}

export async function showETSPushImportIssues(importIssues: any[]): Promise<void> {
  if (!Array.isArray(importIssues) || importIssues.length === 0) return;

  const primaryIssues = importIssues.filter((issue) => !isSecondaryEtsPushIssue(issue));
  const secondaryIssues = importIssues.filter(isSecondaryEtsPushIssue);
  const visibleIssues = primaryIssues.slice(0, 80);
  const omittedCount = Math.max(0, primaryIssues.length - visibleIssues.length);
  const visibleSecondaryIssues = secondaryIssues.slice(0, 80);
  const omittedSecondaryCount = Math.max(0, secondaryIssues.length - visibleSecondaryIssues.length);
  const issueText = formatEtsPushImportIssuesText(importIssues);
  const severityColor = (severity: string) => {
    const value = String(severity || "").toLowerCase();
    if (value === "error") return "rgba(220,40,40,0.12)";
    if (value === "warning") return "rgba(255,165,0,0.14)";
    if (value === "secondary") return "rgba(120,120,120,0.10)";
    return "rgba(120,120,120,0.12)";
  };
  const renderIssue = (issue: any, key: string) => (
    <div
      key={key}
      style={{
        padding: "8px 10px",
        marginBottom: "8px",
        border: "1px solid rgba(128,128,128,0.22)",
        borderRadius: "6px",
        background: severityColor(issue?.severity),
        userSelect: "text",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: "3px" }}>
        {getEtsPushIssueDisplaySeverity(issue)} / {issue?.phase || "import"} / {issue?.code || "issue"}
      </div>
      <div>{issue?.message || ""}</div>
      {formatEtsPushIssueSummary(issue).length > 0 && (
        <div style={{ marginTop: "6px", opacity: 0.86, fontSize: "0.9em" }}>
          {formatEtsPushIssueSummary(issue).map((detail, detailIndex) => (
            <div key={`${key}-summary-${detailIndex}`}>{detail}</div>
          ))}
        </div>
      )}
      {formatEtsPushIssueDetails(issue).length > 0 && (
        <details style={{ marginTop: "6px", opacity: 0.75, fontSize: "0.88em" }}>
          <summary style={{ cursor: "pointer" }}>{itx("details", "Details")}</summary>
          <div style={{ marginTop: "4px" }}>
            {formatEtsPushIssueDetails(issue).map((detail, detailIndex) => (
              <div key={`${key}-detail-${detailIndex}`}>{detail}</div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
  const copyIssuesToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(issueText);
      showToast({ text: itx("export.ETSPushWarningsCopied", "ETS push warnings copied to clipboard.") });
    } catch (ex) {
      showToast({
        isError: true,
        text: `${itx("export.ETSPushWarningsCopyFailed", "Could not copy ETS push warnings")}: ${ex}`,
      });
    }
  };
  const exportIssues = () => {
    downloadData(JSON.stringify(importIssues, null, 2), ".ets-push-warnings.json"); // no itx
  };

  await state.dialogManager.openDialog(
    <BaseDialog
      title={itx("export.ETSPushWarnings", "ETS push warnings")}
      abortCaption={itx("close", "Close")}
      noOkButton={true}
      moreButtons={[
        <button key="copy" className="button" onClick={copyIssuesToClipboard}>
          {itx("copy", "Copy")}
        </button>,
        <button key="export" className="button" onClick={exportIssues}>
          {itx("export", "Export")}
        </button>,
      ]}
      noMinWidth={true}
      bodyNoMinWidth={true}
      style={{
        width: "min(980px, calc(100vw - 40px))",
        maxWidth: "calc(100vw - 40px)",
      }}
    >
      <div style={{ maxWidth: "100%", overflowWrap: "anywhere", wordBreak: "break-word", userSelect: "text" }}>
        <div style={{ padding: "18px 20px 10px 20px" }}>
          {itx(
            "export.ETSPushWarningsIntro",
            secondaryIssues.length > 0
              ? `The push completed with ${primaryIssues.length} push error(s) and ${secondaryIssues.length} warning(s).`
              : "The push completed, but the plugin reported items that may need verification in ETS.",
          )}
        </div>
        <div style={{ padding: "0 20px 20px 20px", maxHeight: "460px", overflow: "auto", userSelect: "text" }}>
          {visibleIssues.length === 0 && (
            <div style={{ marginBottom: "10px", opacity: 0.78 }}>
              {itx("export.ETSPushNoPrimaryWarnings", "No push errors were reported.")}
            </div>
          )}
          {visibleIssues.map((issue: any, index: number) => renderIssue(issue, `ets-push-issue-${index}`))}
          {omittedCount > 0 && (
            <div style={{ opacity: 0.75 }}>
              {itx("export.ETSPushWarningsOmitted", `${omittedCount} additional push error(s) omitted from this view.`)}
            </div>
          )}
          {secondaryIssues.length > 0 && (
            <details style={{ marginTop: "10px", userSelect: "text" }}>
              <summary style={{ cursor: "pointer", fontWeight: 700 }}>
                {itx("export.ETSPushSecondaryWarnings", `${secondaryIssues.length} warning(s)`)}
              </summary>
              <div style={{ marginTop: "8px" }}>
                {visibleSecondaryIssues.map((issue: any, index: number) =>
                  renderIssue(issue, `ets-push-secondary-${index}`),
                )}
                {omittedSecondaryCount > 0 && (
                  <div style={{ opacity: 0.75 }}>
                    {itx(
                      "export.ETSPushSecondaryWarningsOmitted",
                      `${omittedSecondaryCount} additional warning(s) omitted from this view.`,
                    )}
                  </div>
                )}
              </div>
            </details>
          )}
        </div>
      </div>
    </BaseDialog>,
    true,
  );
}

async function showDialogMsg(
  title: string,
  message: string,
  executeSyncOnOkClick?: boolean,
  etsId?: string,
  data?: any,
  includeDefaultValues?: boolean,
  config?: EtsSyncConfig,
  updateGroupAdresses?: boolean,
  etsPin?: string,
): Promise<boolean> {
  const res = await state.dialogManager.openDialog(
    <ConfirmDialog title={title} okCaption={itx("ok", "Ok")} abortCaption={itx("close", "Schließen")} text={message} />,
    true,
  );

  if (res) {
    if (etsId && !state.etsSyncedProjectIds.includes(etsId)) state.etsSyncedProjectIds.push(etsId);
    await syncOperation(config, updateGroupAdresses, state.projectMetadata.etsPin);
  } else {
    showToast({
      text: state.translate("export.operationClosed", "Operation closed by user."),
    });
  }

  return res;
}

export async function etsNavigate(selectedItem: any) {
  if (etsOnline) {
    var item = {
      type: selectedItem.className,
      level: selectedItem?.level,
      etsId: selectedItem.etsId,
      parentEtsId: selectedItem.parent?.etsId,
      grandParentEtsId: selectedItem.parent.parent?.etsId,
    };
    try {
      getActiveEtsSocket()?.sendEvent("navigate", {}, "navigateResult", async (resData: any) => {
        // no itx
        if (resData?.status == "OK") {
          logger.log(resData?.data);
        }
      });
    } catch (ex) {
      logger.error("Error during ets navigation");
      showToast({
        isError: true,
        text: itx(
          "export.navigationFailedverifyEts",
          "Navigation failed. Please verify that ETS and Estada Plugin are active.",
        ),
      });
    }
  }
}

export async function uploadExportRequestToServer() {
  // const knxData = await createKnxData(includeDefaultValues, config);
  const knxData = await createKnxData(false, undefined, true);
  if (!knxData) {
    return;
  }

  if (addressOverflow) {
    showToast({
      text: addressOverflowTextMsgT(),
    });
    return;
  }

  const zip = new JSZip();
  const knxDataString = JSON.stringify(knxData);
  zip.file("data", knxDataString);

  const blob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE", // no itx
    compressionOptions: {
      level: 9,
    },
    encodeFileName: (data) => encodeToCP437(data),
  });

  const data = new FormData();
  data.set("data", blob, "data");

  const response = await axios.post(
    `/api/projectexport/uploadXchangeData/${cleanEncode(state.projectMetadata.id)}`,
    data,
  );
}

/**
 * Checks whether the local ETS6 WebSocket server (ws://localhost:1235/) is reachable.
 * Used as a fallback when the cloud relay is unavailable (offline / same-machine mode).
 */
export async function checkIfLocalEtsOnline(): Promise<EtsLocalStatus> {
  openLocalEtsWebSocket(); // ensure a socket is created/reused

  // Wait for the socket to open before sending the ping.
  // This is important on the very first call when the panel opens while ETS6 is already running —
  // the WebSocket handshake is async and may not be complete yet.
  try {
    await localEtsWebSocket!.waitForOpen(2000);
  } catch {
    closeLocalEtsWebSocket();
    return ETS_LOCAL_OFFLINE_STATUS;
  }

  return new Promise((resolve) => {
    try {
      let settled = false;
      let t: ReturnType<typeof setTimeout>;
      const finish = (status: EtsLocalStatus) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        localEtsWebSocket?.removeCallback("pingResult"); // no itx
        if (!status.status && localEtsWebSocket?.isClosed) {
          closeLocalEtsWebSocket();
        }
        resolve(status);
      };
      t = setTimeout(() => finish(ETS_LOCAL_OFFLINE_STATUS), 2500);
      localEtsWebSocket!.sendEvent(
        "ping", // no itx
        {},
        "pingResult", // no itx
        (resData: any) => {
          // no itx
          if (resData?.status === true) {
            finish({
              status: true,
              version: resData.version || "",
              buildDate: resData.buildDate || "",
              projectName: resData.projectName || "",
              projectId: resData.projectId || "",
              projectGuid: resData.projectGuid || "",
              selectedProjectId: resData.selectedProjectId || resData.projectId || "",
              projects: Array.isArray(resData.projects) ? resData.projects : [],
            });
          } else {
            finish(ETS_LOCAL_OFFLINE_STATUS);
          }
        },
      );
    } catch {
      closeLocalEtsWebSocket();
      resolve(ETS_LOCAL_OFFLINE_STATUS);
    }
  });
}

export async function checkIfEtsOnline(): Promise<EtsRelayStatus> {
  const primaryStatus = await pingEtsRelayOnce();
  if (primaryStatus.status) {
    rememberSuccessfulRelayStatus(primaryStatus);
    return primaryStatus;
  }

  if (isLastSuccessfulRelayStillSelected()) {
    relayPingMissCount++;
    if (relayPingMissCount < RELAY_PING_MISSES_BEFORE_FALLBACK) {
      return lastSuccessfulRelayStatus || primaryStatus;
    }
  } else {
    relayPingMissCount = 0;
  }

  // A connected but silent primary relay should try the configured fallback relay
  // before the frontend declares ETS offline. Do this only after the known-good
  // relay has missed more than once, otherwise a slow ping causes visible flicker.
  if (etsWebSocket?.tryFallbackUrl()) {
    const fallbackStatus = await pingEtsRelayOnce();
    if (fallbackStatus.status) {
      rememberSuccessfulRelayStatus(fallbackStatus);
    }
    return fallbackStatus;
  }

  return primaryStatus;
}

function rememberSuccessfulRelayStatus(status: EtsRelayStatus) {
  lastSuccessfulRelayUrl = etsWebSocket?.getCurrentUrl() || "";
  lastSuccessfulRelayRoom = etsWebSocket?.room || "";
  lastSuccessfulRelayStatus = status;
  relayPingMissCount = 0;
}

function isLastSuccessfulRelayStillSelected(): boolean {
  return Boolean(
    lastSuccessfulRelayUrl &&
      etsWebSocket?.getCurrentUrl() === lastSuccessfulRelayUrl &&
      (etsWebSocket?.room || "") === lastSuccessfulRelayRoom,
  );
}

async function pingEtsRelayOnce(): Promise<EtsRelayStatus> {
  if (!etsWebSocket) return ETS_RELAY_OFFLINE_STATUS;

  if (!etsWebSocket.isConnected) {
    try {
      await etsWebSocket.waitForOpen(3000);
    } catch {
      return ETS_RELAY_OFFLINE_STATUS;
    }
  }

  return new Promise((resolve) => {
    try {
      let settled = false;
      let t: ReturnType<typeof setTimeout>;
      const finish = (status: EtsRelayStatus) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        etsWebSocket?.removeCallback("pingResult"); // no itx
        resolve(status);
      };
      t = setTimeout(() => finish(ETS_RELAY_OFFLINE_STATUS), 3000);

      etsWebSocket?.sendEvent(
        "ping", // no itx
        {},
        "pingResult", // no itx
        (resData: any) => {
          if (resData?.status == true) {
            finish({
              status: true,
              pin: resData.pin || "",
              version: resData.version || "",
              buildDate: resData.buildDate || "",
              projectName: resData.projectName || "",
              projectId: resData.projectId || "",
              projectGuid: resData.projectGuid || "",
            });
          } else {
            finish(ETS_RELAY_OFFLINE_STATUS);
          }
        },
      );
    } catch (ex) {
      resolve(ETS_RELAY_OFFLINE_STATUS);
    }
  });
}

export async function checkIfEtsOnlineViaHttp(): Promise<boolean> {
  if (await checkIfEtsOnlineViaLocalWs()) return true;

  return await checkIfEtsOnlineViaHttpFallback();
}

export async function checkIfEtsOnlineViaLocalWs(): Promise<boolean> {
  const localStatus = await checkIfLocalEtsOnline();
  if (localStatus.status) {
    etsOnline = true;
    return true;
  }

  return false;
}

export async function checkIfEtsOnlineViaHttpFallback(): Promise<boolean> {
  try {
    const response = await etsFetch(`${LocalEtsAppUrl}/ping`, {
      method: "GET", // no itx
      timeout: 100,
    });
    etsOnline = response.ok;
  } catch (ex) {
    return false;
  }

  return etsOnline;
}

export async function checkIfBackofficeOnline() {
  try {
    const response = await axios.get(`/api/getBackofficeStatus`);
    if (response.data?.MESSAGE == "Online") {
      return true;
    }
  } catch (ex) {
    logger.log("", ex);
  }
  throw Error("Backoffice is Offline"); // no itx
}

export function reopenEtsDialog(etsPin?: string) {
  state.dialogManager.openDialog(<EtsDialog autoPasteLastPinOnOpen />, true);
}
